<template>
  <AnalyticsWidget
    title="Lean Manufacturing Summary"
    subTitle="Lean manufacturing summary for production stages during the date range"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="p-2 h-100">
      <ReportFilterPopover
        headerTitle="Lean Summary Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyLeanSummaryFilter"
        @reset="resetLeanSummaryFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingLeanSummaryFilter.startDate"
              :endDate="editingLeanSummaryFilter.endDate"
              @updateStartDate="editingLeanSummaryFilter.startDate = $event"
              @updateEndDate="editingLeanSummaryFilter.endDate = $event"
            />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedLeanSummaryFilter.startDate | formatDate}} - {{appliedLeanSummaryFilter.endDate | formatDate}}
          </div>
        </template>
      </ReportFilterPopover>

      <LoadingPanel :loading="loading" />
      <AnalyticsBreakdownTable v-if="!loading">
        <template v-slot:header>
          <tr>
            <th></th>
            <th><Tooltip label="Total number of panels that were or are due at this stage during the date range" helpStyle>Overall Demand</Tooltip></th>
            <th><Tooltip label="Average time available to complete a panel to satisfy customer demand" helpStyle>Avg. Takt Time</Tooltip></th>
            <th><Tooltip label="Average time a panel spent idle waiting at this stage to begin production" helpStyle>Avg. Idle Session Time</Tooltip></th>
            <th><Tooltip label="Average time a panel spent in production at this stage" helpStyle>Avg. Production Session Time</Tooltip></th>
            <th><Tooltip label="Number of panels that were due or overdue today" helpStyle>Today&apos;s Demand</Tooltip></th>
            <th><Tooltip label="Average time available to complete a panel to satisfy customer demand today" helpStyle>Today&apos;s Takt Time</Tooltip></th>
          </tr>
        </template>
        <template v-slot:body>
          <tr v-for="stageInfo of stageSummaryData" :key="stageInfo.id">
            <td><LinkProductionStage :id="stageInfo.id" :name="stageInfo.name" /></td>
            <td>{{stageInfo.customerDemandCount}}</td>
            <td>{{formatDecimal(stageInfo.taktTimeMinutes, 2, true)}} mins</td>
            <td>{{displaySessionValue(stageInfo, ProductionPanelSessionTypes.idle, 'averageSessionTime')}}</td>
            <td>{{displaySessionValue(stageInfo, ProductionPanelSessionTypes.production, 'averageSessionTime')}}</td>
            <td>{{todayStageCustomerDemandCount(stageInfo.id)}}</td>
            <td>{{formatDecimal(todayStageTaktTime(stageInfo.id) || 0, 2, true)}} mins</td>
          </tr>

          <!-- Totals / Averages -->
          <tr class="font-weight-bold">
            <td class="border-top">Totals / Averages</td>
            <td class="border-top">{{totalCustomerDemandCount}}</td>
            <td class="border-top">{{formatDecimal(averageTaktTime, 2, true)}} mins</td>
            <td class="border-top">{{formatDecimal(averageIdleSessionTime, 2, true)}} mins</td>
            <td class="border-top">{{formatDecimal(averageProductionSessionTime, 2, true)}} mins</td>
            <td class="border-top">{{totalTodayCustomerDemandCount}}</td>
            <td class="border-top">{{formatDecimal(averageTodayTaktTime, 2, true)}} mins</td>
          </tr>
        </template>

      </AnalyticsBreakdownTable>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { ProductionPanelSessionTypes } from '@/store/types'

export default {
  name: 'ProductionLeanSummaryWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup
  },
  props: {
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  computed: {
    totalCustomerDemandCount() {
      return this.stageSummaryData.reduce((sum, stage) => sum + stage.customerDemandCount, 0)
    },
    totalTodayCustomerDemandCount() {
      return this.todayStageSummaryData.reduce((sum, stage) => sum + stage.customerDemandCount, 0)
    },
    averageTaktTime() {
      const totalTaktTime = this.stageSummaryData.reduce((sum, stage) => sum + (stage.taktTimeMinutes || 0), 0);
      const count = this.stageSummaryData.filter(stage => stage.taktTimeMinutes > 0).length;
      return count > 0 ? (totalTaktTime / count) : 0;
    },
    averageTodayTaktTime() {
      const totalTodayTaktTime = this.todayStageSummaryData.reduce((sum, stage) => sum + (stage.taktTimeMinutes || 0), 0);
      const count = this.todayStageSummaryData.filter(stage => stage.taktTimeMinutes > 0).length;
      return count > 0 ? (totalTodayTaktTime / count) : 0;
    },
    averageIdleSessionTime() {
      return this.calculateAverageSessionTime(ProductionPanelSessionTypes.idle);
    },
    averageProductionSessionTime() {
      return this.calculateAverageSessionTime(ProductionPanelSessionTypes.production);
    },
  },
  data() {
    return {
      loading: true,
      stageSummaryData: {},
      todayStageSummaryData: {},

      defaultLeanSummaryFilter: {    
        startDate: moment().local().subtract(3, 'months').startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('month').toDate().toISOString(),
      },
      editingLeanSummaryFilter: {},
      appliedLeanSummaryFilter: {},
    }
  },
  created() {
    this.ProductionPanelSessionTypes = ProductionPanelSessionTypes
  },
  mounted() {
    this.editingLeanSummaryFilter = _.clone(this.defaultLeanSummaryFilter)
    this.appliedLeanSummaryFilter = _.clone(this.defaultLeanSummaryFilter)

    this.loading = true
    this.requestData()
  },
  beforeDestroy() {
  },
  methods: {
    requestData() {
      var params = {
        startDate: this.appliedLeanSummaryFilter.startDate,
        endDate: this.appliedLeanSummaryFilter.endDate
      }
      var todayParams = {
        startDate: moment().local().startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      }

      this.loading = true
      Promise.all([
        this.$apiService.analyticsService.getProductionLeanSummary(params),
        this.$apiService.analyticsService.getProductionLeanSummary(todayParams)
      ])
      .then(results => {
        this.stageSummaryData = results[0].data
        this.todayStageSummaryData = results[1].data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    },
    reloadData() {
      this.requestData()
    },
    applyLeanSummaryFilter() {
      this.appliedLeanSummaryFilter = _.clone(this.editingLeanSummaryFilter)

      this.reloadData()
    },
    resetLeanSummaryFilter() {
      this.editingLeanSummaryFilter = _.clone(this.defaultLeanSummaryFilter)
      this.appliedLeanSummaryFilter = _.clone(this.defaultLeanSummaryFilter)

      this.reloadData()
    },
    displaySessionValue(stageSummaryInfo, sessionType, fieldName) {
      // Note: Sessions are in seconds
      if(stageSummaryInfo.summaryList) {
        var summaryItem = stageSummaryInfo.summaryList.find(item => item.type == sessionType)
        if(summaryItem && summaryItem[fieldName] !== undefined) {
          return this.formatDecimal(summaryItem[fieldName] / 60, 2, true) + ' mins'
        }
      }

      return '-'
    },
    calculateAverageSessionTime(sessionType) {
      const sessionTimeList = this.stageSummaryData.reduce((list, stage) => {
        if (stage.summaryList) {
          const session = stage.summaryList.find(item => item.type == sessionType);
          if (session && session.averageSessionTime > 0) {
            list.push(session.averageSessionTime);
          }
        }
        return list;
      }, []);

      const totalSessionTime = sessionTimeList.reduce((sum, time) => sum + time, 0);
      return sessionTimeList.length > 0 ? ((totalSessionTime / 60.0) / sessionTimeList.length) : 0;
    },
    todayStageCustomerDemandCount(stageId) {
      // Get the customer demand count for the stage today
      const stage = this.todayStageSummaryData.find(s => s.id === stageId);
      return stage ? stage.customerDemandCount : 0;
    },
    todayStageTaktTime(stageId) {
      // Get the takt time for the stage today
      const stage = this.todayStageSummaryData.find(s => s.id === stageId);
      return stage ? stage.taktTimeMinutes : 0;
    }
  }
}
</script>